import { IconButton, LuIcon } from "@overmap-ai/blocks"
import { indigo } from "@radix-ui/colors"
import * as turf from "@turf/turf"
import type { Feature, Geometry, Point } from "geojson"
import type { LineLayerSpecification } from "mapbox-gl"
import { memo, useCallback, useId, useMemo, useState } from "react"
import type { MarkerDragEvent } from "react-map-gl/mapbox"

import { modelIconMapping } from "../../mappings/icons"
import { MapSlotLayerId } from "../enums"
import { transformStretch } from "../utils"
import { Layer, Marker, Source } from "./memo"

const LINE_PAINT: LineLayerSpecification["paint"] = {
	"line-color": indigo.indigo9,
	"line-width": 2,
	"line-dasharray": [2, 2],
}

const ROTATE_POINT_SCALE = 1.2

interface GeometryEditorProps {
	geometry: Geometry
	onGeometryChange: (geometry: Geometry) => void
	moveable?: boolean
	scalable?: boolean
	stretchable?: boolean
	rotateable?: boolean
}

export const GeometryEditor = memo<GeometryEditorProps>((props) => {
	const {
		geometry,
		onGeometryChange,
		moveable = true,
		scalable = true,
		stretchable = true,
		rotateable = true,
	} = props

	const id = useId()

	const [bbox, setBbox] = useState(() => {
		return turf.bboxPolygon(turf.bbox(geometry))
	})

	const rotatePoint = useMemo(() => {
		const bboxPoints = turf.explode(turf.transformScale(bbox, ROTATE_POINT_SCALE)).features
		return bboxPoints[2]
	}, [bbox])

	const [
		centerPoint,
		bboxSwPoint,
		bboxSePoint,
		bboxNePoint,
		bboxNwPoint,
		bboxNPoint,
		bboxEPoint,
		bboxSPoint,
		bboxWPoint,
	] = useMemo(() => {
		const bboxPoints = turf.explode(bbox).features
		bboxPoints.pop()

		const [bboxSwPoint, bboxSePoint, bboxNePoint, bboxNwPoint] = bboxPoints

		return [
			turf.centroid(bbox),
			bboxSwPoint,
			bboxSePoint,
			bboxNePoint,
			bboxNwPoint,
			turf.midpoint(bboxNwPoint, bboxNePoint),
			turf.midpoint(bboxNePoint, bboxSePoint),
			turf.midpoint(bboxSePoint, bboxSwPoint),
			turf.midpoint(bboxSwPoint, bboxNwPoint),
		]
	}, [bbox])

	// MOVE
	const handleMove = useCallback(
		(e: MarkerDragEvent) => {
			const { lngLat } = e

			const from = centerPoint
			const to = turf.point(lngLat.toArray())

			const distance = turf.distance(from, to)
			const bearing = turf.bearing(from, to)

			onGeometryChange(turf.transformTranslate(geometry, distance, bearing))
			setBbox(turf.transformTranslate(bbox, distance, bearing))
		},
		[bbox, centerPoint, onGeometryChange, geometry],
	)

	// ROTATE
	const handleRotate = useCallback(
		(e: MarkerDragEvent) => {
			const { lngLat } = e

			const bearingFrom = turf.bearing(centerPoint, rotatePoint)
			const bearingTo = turf.bearing(centerPoint, lngLat.toArray())

			let degrees = bearingTo - bearingFrom

			if (degrees > 180) {
				degrees -= 360
			} else if (degrees < -180) {
				degrees += 360
			}

			onGeometryChange(turf.transformRotate(geometry, degrees, { pivot: centerPoint }))
			setBbox(turf.transformRotate(bbox, degrees))
		},
		[bbox, centerPoint, onGeometryChange, geometry, rotatePoint],
	)

	// SCALE
	const handleScale = useCallback(
		(e: MarkerDragEvent, originPoint: Feature<Point>, point: Feature<Point>) => {
			const { lngLat } = e

			const newPoint = turf.point(lngLat.toArray())
			const distanceOriginal = turf.distance(centerPoint, point)
			const distanceNew = turf.distance(centerPoint, newPoint)

			const factor = distanceNew / distanceOriginal

			onGeometryChange(turf.transformScale(geometry, factor, { origin: originPoint }))
			setBbox(turf.transformScale(bbox, factor, { origin: originPoint }))
		},
		[bbox, centerPoint, onGeometryChange, geometry],
	)

	const handleScaleNW = useCallback(
		(e: MarkerDragEvent) => {
			handleScale(e, bboxSePoint, bboxNwPoint)
		},
		[bboxNwPoint, bboxSePoint, handleScale],
	)

	const handleScaleNE = useCallback(
		(e: MarkerDragEvent) => {
			handleScale(e, bboxSwPoint, bboxNePoint)
		},
		[bboxNePoint, bboxSwPoint, handleScale],
	)

	const handleScaleSW = useCallback(
		(e: MarkerDragEvent) => {
			handleScale(e, bboxNePoint, bboxSwPoint)
		},
		[bboxNePoint, bboxSwPoint, handleScale],
	)

	const handleScaleSE = useCallback(
		(e: MarkerDragEvent) => {
			handleScale(e, bboxNwPoint, bboxSePoint)
		},
		[bboxNwPoint, bboxSePoint, handleScale],
	)

	// STRETCH
	const handleStretch = useCallback(
		(
			e: MarkerDragEvent,
			endpoint1: Feature<Point>,
			endpoint2: Feature<Point>,
			originPoint: Feature<Point>,
			point: Feature<Point>,
		) => {
			const { lngLat } = e

			const originLine = turf.lineString([endpoint1.geometry.coordinates, endpoint2.geometry.coordinates])

			const newPoint = turf.point(lngLat.toArray())
			const hypotenuse = turf.distance(originPoint, newPoint)
			const angle = turf.angle(point, originPoint, newPoint)
			const adjacent = hypotenuse * Math.cos(angle * (Math.PI / 180))

			const foundPoint = turf.destination(originPoint, adjacent, turf.bearing(originPoint, point))

			const bearing = turf.bearing(point, foundPoint)
			const distance = turf.distance(point, foundPoint)

			onGeometryChange(transformStretch(geometry, originLine, distance, bearing))
			setBbox(transformStretch(bbox, originLine, distance, bearing))
		},
		[bbox, onGeometryChange, geometry],
	)

	const handleStretchN = useCallback(
		(e: MarkerDragEvent) => {
			handleStretch(e, bboxSwPoint, bboxSePoint, bboxSPoint, bboxNPoint)
		},
		[bboxNPoint, bboxSPoint, bboxSePoint, bboxSwPoint, handleStretch],
	)

	const handleStretchE = useCallback(
		(e: MarkerDragEvent) => {
			handleStretch(e, bboxSwPoint, bboxNwPoint, bboxWPoint, bboxEPoint)
		},
		[bboxEPoint, bboxNwPoint, bboxSwPoint, bboxWPoint, handleStretch],
	)

	const handleStretchS = useCallback(
		(e: MarkerDragEvent) => {
			handleStretch(e, bboxNePoint, bboxNwPoint, bboxNPoint, bboxSPoint)
		},
		[bboxNPoint, bboxNePoint, bboxNwPoint, bboxSPoint, handleStretch],
	)

	const handleStretchW = useCallback(
		(e: MarkerDragEvent) => {
			handleStretch(e, bboxNePoint, bboxSePoint, bboxEPoint, bboxWPoint)
		},
		[bboxEPoint, bboxNePoint, bboxSePoint, bboxWPoint, handleStretch],
	)

	return (
		<>
			<Source id={`${id}-source`} type="geojson" data={bbox}>
				<Layer id={`${id}-line`} paint={LINE_PAINT} type="line" beforeId={MapSlotLayerId.DRAW} />
			</Source>

			{/* move */}
			{moveable && (
				<Marker
					anchor="center"
					longitude={centerPoint.geometry.coordinates[0]}
					latitude={centerPoint.geometry.coordinates[1]}
					draggable
					onDrag={handleMove}
				>
					<IconButton size="xs" radius="full" accentColor="info" className="flex!">
						<LuIcon icon={modelIconMapping.move} />
					</IconButton>
				</Marker>
			)}

			{/* rotate */}
			{rotateable && (
				<Marker
					anchor="center"
					longitude={rotatePoint.geometry.coordinates[0]}
					latitude={rotatePoint.geometry.coordinates[1]}
					draggable
					onDrag={handleRotate}
				>
					<IconButton size="xs" radius="full" accentColor="info" className="flex!">
						<LuIcon icon={modelIconMapping.refresh} />
					</IconButton>
				</Marker>
			)}

			{scalable && (
				<>
					{/* nw */}
					<Marker
						anchor="center"
						longitude={bboxNwPoint.geometry.coordinates[0]}
						latitude={bboxNwPoint.geometry.coordinates[1]}
						draggable
						onDrag={handleScaleNW}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>

					{/* ne */}
					<Marker
						anchor="center"
						longitude={bboxNePoint.geometry.coordinates[0]}
						latitude={bboxNePoint.geometry.coordinates[1]}
						draggable
						onDrag={handleScaleNE}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>

					{/* sw */}
					<Marker
						anchor="center"
						longitude={bboxSwPoint.geometry.coordinates[0]}
						latitude={bboxSwPoint.geometry.coordinates[1]}
						draggable
						onDrag={handleScaleSW}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>

					{/* se */}
					<Marker
						anchor="center"
						longitude={bboxSePoint.geometry.coordinates[0]}
						latitude={bboxSePoint.geometry.coordinates[1]}
						draggable
						onDrag={handleScaleSE}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>
				</>
			)}

			{/* N */}
			{stretchable && (
				<>
					<Marker
						anchor="center"
						longitude={bboxNPoint.geometry.coordinates[0]}
						latitude={bboxNPoint.geometry.coordinates[1]}
						draggable
						onDrag={handleStretchN}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>

					{/* E */}
					<Marker
						anchor="center"
						longitude={bboxEPoint.geometry.coordinates[0]}
						latitude={bboxEPoint.geometry.coordinates[1]}
						draggable
						onDrag={handleStretchE}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>

					{/* S */}
					<Marker
						anchor="center"
						longitude={bboxSPoint.geometry.coordinates[0]}
						latitude={bboxSPoint.geometry.coordinates[1]}
						draggable
						onDrag={handleStretchS}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>

					{/* W */}
					<Marker
						anchor="center"
						longitude={bboxWPoint.geometry.coordinates[0]}
						latitude={bboxWPoint.geometry.coordinates[1]}
						draggable
						onDrag={handleStretchW}
					>
						<IconButton
							size="xs"
							radius="full"
							accentColor="info"
							className="block! size-3! border border-white"
						/>
					</Marker>
				</>
			)}
		</>
	)
})
GeometryEditor.displayName = "GeometryEditor"
